import React, { useState, useEffect } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import RenewMotorLayout from "../renewalMotorPolicy/renewMotorLayout";
import TitleDescription from "../../../components/newMotorPolicy/titleDescription/titleDescription";
import Input from "../../../components/common/Input/Input";
import useValidation from "../../../hooks/useValidation";
import useLanguageTranslate from "../../../hooks/useLanguageTranslate";
import useTrackClaimFlow, { 
  TRACK_CLAIM_LOGGED_IN, 
  TRACK_CLAIM_LOGGED_OUT 
} from "../../../hooks/useTrackClaimFlow";
import { 
  setTrackClaimFormData,
  clearTrackClaimFormData 
} from "../../../redux/features/trackClaimFlowSlice";
import { getClaimDetails } from "../../../redux/features/crm/crmClaimSlice";
import { isLoggedIn } from "../../../config/config";
import CryptoJS from "crypto-js";

export const TRACK_CLAIM_CPR_CLAIM_NUMBER = "TRACK_CLAIM_CPR_CLAIM_NUMBER";

const TrackClaimCprClaimNumberPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const translate = useLanguageTranslate();
  const { handleFlowChange } = useTrackClaimFlow();
  const { errors, validateFields } = useValidation();
  
  const [formData, setFormData] = useState({ cprNumber: "", claimNumber: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  
  const userLoggedIn = isLoggedIn();

  useEffect(() => {
    dispatch(clearTrackClaimFormData());
    if (userLoggedIn) {
      handleFlowChange(TRACK_CLAIM_LOGGED_IN, 0);
    } else {
      handleFlowChange(TRACK_CLAIM_LOGGED_OUT, 0);
    }
  }, [userLoggedIn]);

  const validateForm = (data) => {
    const validationConfig = [
      {
        name: "cprNumber",
        validations: ["required"],
      },
      {
        name: "claimNumber",
        validations: ["required"],
      },
    ];
    return validateFields(validationConfig, data || formData);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    const update = { ...formData, [name]: value };
    setFormData(update);
    validateForm(update);
  };

  const encryptClaimNumber = (claimNumber) => {
    const secretKey = "i-hate-meej";
    const encryptedValue = CryptoJS.AES.encrypt(claimNumber, secretKey).toString();
    return encryptedValue
      .split("+").join("-")
      .split("/").join("_")
      .split("=").join("");
  };

  const continueClickHandler = async () => {
    if (!validateForm()) return;
    
    setIsSubmitting(true);
    
    try {
      dispatch(setTrackClaimFormData(formData));

      // Both logged-in and logged-out users: fetch claim details directly
      // (no OTP step). Mirrors the quick renewal revert per business request.
      const response = await dispatch(
        getClaimDetails({ caseNumber: formData.claimNumber })
      ).unwrap();

      // Some backends return HTTP 200 with an error body, e.g.
      // { Error: "Something went wrong", statusCode: 400 }. Treat any
      // response without a Claim, or with an explicit error/non-2xx
      // statusCode, as a failed lookup.
      const hasError =
        !response ||
        response.Error ||
        (typeof response.statusCode === "number" && response.statusCode >= 400) ||
        !response.Claim;

      if (hasError) {
        throw response || new Error("Claim not found");
      }

      const encryptedClaimNumber = encryptClaimNumber(response.Claim.CaseNumber);
      navigate(`/motor-insurance/claim-details/${encryptedClaimNumber}`);
    } catch (e) {
      console.error(e);
      toast.error(
        translate(
          "We couldn't locate a claim with the details provided. Please verify your CPR and claim number and try again.",
          "تعذّر العثور على مطالبة بالبيانات المُدخلة. يرجى التحقق من رقم البطاقة الشخصية ورقم المطالبة والمحاولة مرة أخرى."
        )
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <RenewMotorLayout
      show_close_btn
      is_hide_back_btn={true}
      btn_onClick={continueClickHandler}
      isLoading={isSubmitting}
      btn_disabled={isSubmitting}
    >
      <div className="max-w-[100%] w-[500px] mx-auto bg-white max767:p-5 max767:pt-0">
        <TitleDescription
          title={translate("Track Claim", "تتبع المطالبة")}
          description={translate(
            "To locate your claim, please provide the following information.",
            "لتحديد مطالبتك، يرجى تقديم المعلومات التالية."
          )}
        />
        <Input
          name="cprNumber"
          type="text"
          label={translate("CPR Number", "رقم البطاقة الشخصية")}
          value={formData?.cprNumber}
          error={errors?.cprNumber}
          handleChange={handleChange}
        />
        <Input
          name="claimNumber"
          type="text"
          label={translate("Claim Number", "رقم المطالبة")}
          value={formData?.claimNumber}
          error={errors?.claimNumber}
          handleChange={handleChange}
        />
      </div>
    </RenewMotorLayout>
  );
};

export default TrackClaimCprClaimNumberPage;
